/**
 * Deleting an area, and refusing to when that would rewrite history
 * (RESP2c).
 *
 * The rule this dialog exists to make legible: **work filed in an area
 * is the organisation's record of what happened.** An area with a single
 * task or report behind it is never deleted, however old that work is —
 * the server refuses with 409 `responsibility_area_in_use`, and this
 * says so before the request is sent rather than after.
 *
 * So there are two states, decided by the usage figures already loaded
 * for the selected area:
 *
 * * **deletable** — nothing is filed in it. A plain confirmation.
 * * **blocked** — something is. The counts are shown, and the offered
 *   action is DEACTIVATE, which is the thing that actually solves the
 *   problem the person came here with ("stop offering this area").
 *
 * The blocked state is also reachable from a rejected request: between
 * reading a count of zero and pressing Löschen, a colleague can file a
 * task, and Migration 156's `ON DELETE RESTRICT` catches it. That 409
 * flips this dialog into the same blocked state instead of showing a
 * toast and closing — the person's next action is the same either way,
 * and it is right here.
 *
 * The raw server message and the constraint name never appear.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

import { ResponsiveModal } from "../../../components/ui/responsive-modal";
import { Skeleton } from "../../../components/ui/skeleton";
import { deleteResponsibilityArea } from "../../../api/adminData";

/** Counts from either the usage endpoint or a 409's `details`. */
function blockedCounts(usage, serverDetails) {
  const source = serverDetails || usage || {};
  return {
    tasks: source.task_count_total ?? 0,
    reports: source.work_report_count_total ?? 0,
  };
}

export default function ResponsibilityAreaDeleteDialog({
  open,
  area,
  usage,
  usageLoading,
  onOpenChange,
  onDeleted,
  onDeactivate,
}) {
  const [busy, setBusy] = useState(false);
  // Set only when the SERVER refuses — a race the pre-check could not
  // have seen. Kept apart from `usage` so the two sources never merge
  // into one ambiguous number.
  const [serverBlock, setServerBlock] = useState(null);

  useEffect(() => {
    if (open) setServerBlock(null);
  }, [open, area]);

  if (!area) return null;

  const blocked = Boolean(serverBlock) || usage?.has_historical_usage === true;
  const counts = blockedCounts(usage, serverBlock);

  const confirmDelete = async () => {
    if (busy) return; // double-submit guard
    setBusy(true);
    try {
      const result = await deleteResponsibilityArea(area.id);
      onOpenChange(false);
      toast.success(`„${result?.label || area.label}" wurde gelöscht.`);
      onDeleted?.(area);
    } catch (err) {
      if (err?.code === "responsibility_area_in_use") {
        // Not a toast-and-close: the dialog becomes the explanation.
        setServerBlock(err.details || {});
      } else if (err?.code === "responsibility_area_system_delete_forbidden") {
        onOpenChange(false);
        toast.error("Systembereiche können nicht gelöscht werden.");
      } else {
        toast.error(
          err?.message || "Der Bereich konnte nicht gelöscht werden."
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onDeactivate?.(area);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={busy ? () => {} : onOpenChange}
      title={blocked ? "Bereich wird verwendet" : "Bereich löschen"}
      description={
        blocked
          ? `„${area.label}" kann nicht gelöscht werden.`
          : `„${area.label}" wird dauerhaft entfernt.`
      }
    >
      <div className="space-y-4">
        {usageLoading && !serverBlock ? (
          <>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </>
        ) : blocked ? (
          <>
            <div className="flex items-start gap-3 rounded-[10px] border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
              <AlertTriangle
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
              />
              <p className="m-0 text-[13px] leading-relaxed text-amber-900 dark:text-amber-200">
                In diesem Bereich ist bereits Arbeit erfasst. Diese Einträge
                sind der Nachweis darüber, was tatsächlich geschehen ist, und
                bleiben erhalten.
              </p>
            </div>

            <dl className="m-0 divide-y divide-border rounded-[10px] border border-border">
              <div className="flex items-baseline justify-between gap-4 px-3 py-2">
                <dt className="text-[13px] text-muted-foreground">
                  Aufgaben insgesamt
                </dt>
                <dd className="m-0 text-[13px] font-semibold text-foreground">
                  {counts.tasks}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 px-3 py-2">
                <dt className="text-[13px] text-muted-foreground">
                  Arbeitsrapporte insgesamt
                </dt>
                <dd className="m-0 text-[13px] font-semibold text-foreground">
                  {counts.reports}
                </dd>
              </div>
            </dl>

            <p className="m-0 text-[13px] leading-relaxed text-muted-foreground">
              Ein deaktivierter Bereich wird für neue Aufgaben und
              Arbeitsrapporte nicht mehr angeboten. Bestehende Einträge
              bleiben unverändert zugeordnet.
            </p>
          </>
        ) : (
          <p className="m-0 text-[13px] leading-relaxed text-muted-foreground">
            In diesem Bereich ist noch keine Arbeit erfasst. Er kann entfernt
            werden, ohne dass Aufgaben oder Arbeitsrapporte verloren gehen.
            Diese Aktion lässt sich nicht rückgängig machen.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => onOpenChange(false)}
            className="rounded-[10px] border border-border px-4 py-2 text-[13px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            Abbrechen
          </button>
          {blocked ? (
            area.is_active ? (
              <button
                type="button"
                disabled={busy}
                onClick={deactivate}
                className="rounded-[10px] bg-violet-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              >
                {busy ? "Wird deaktiviert …" : "Stattdessen deaktivieren"}
              </button>
            ) : null
          ) : (
            <button
              type="button"
              disabled={busy || usageLoading}
              onClick={confirmDelete}
              className="rounded-[10px] bg-destructive px-4 py-2 text-[13px] font-semibold text-destructive-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            >
              {busy ? "Wird gelöscht …" : "Endgültig löschen"}
            </button>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
}
